import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import get from 'lodash.get'
import FlexRow from '../../../molecules/FlexRow'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import DDPTag from '../widgets/DDPTag'

/**
 * A table component for displaying data from the Denali Data Pipeline.
 * @param pipeline
 * @param variables
 * @param seriesAccessor
 * @param valueAccessor
 * @param itemIcon
 * @param className
 * @param style
 * @param itemClassName
 * @param itemStyle
 * @param component
 * @param componentProps
 * @returns {JSX.Element|null}
 * @constructor
 */
function DDPTagList ({
  pipeline,
  variables,
  seriesAccessor,
  valueAccessor,
  itemIcon,
  className,
  style,
  itemClassName,
  itemStyle,
  component,
  componentProps
}) {
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  const tags = useMemo(() => {
    const series = get(data, seriesAccessor) || []
    return series.map((item, index) => ({
      value: item,
      id: `tag_${index}`
    }))
  }, [data, seriesAccessor])

  if (isLoading) {
    return (
      <FlexRow className={clsx(className)} style={style} />
    )
  }

  if (component) {
    return (
      <FlexRow className={clsx(className)} style={style}>
        {(tags || []).map((tag) => (
          React.cloneElement(component, {
            ...(componentProps || {}),
            key: tag.id,
            item: tag.value
          })
        ))}
      </FlexRow>
    )
  }

  return (
    <FlexRow className={clsx(className)} style={style}>
      {(tags || []).map((tag) => (
        <DDPTag
          key={tag.id}
          item={tag.value}
          className={itemClassName}
          style={itemStyle}
          accessor={valueAccessor}
          icon={itemIcon}
        />
      ))}
    </FlexRow>
  )
}

DDPTagList.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** Default values for variables */
  variables: PropTypes.object,

  /** Finds the array in the pipeline response */
  seriesAccessor: PropTypes.string,

  /** Finds the item value in the pipeline response */
  valueAccessor: PropTypes.string,

  /** if specified, displays an icon per tag */
  itemIcon: PropTypes.string,

  className: PropTypes.string,
  style: PropTypes.object,

  itemClassName: PropTypes.string,
  itemStyle: PropTypes.object,

  component: PropTypes.node,
  componentProps: PropTypes.object
}

DDPTagList.defaultProps = {
  seriesAccessor: 'data',
  valueAccessor: 'value'
}

export default DDPTagList
